import axios from 'axios';
import { extractErrorMessage } from './errorUtils';

// Use env var if set (local dev), otherwise default to relative path (production multi-tenant)
const API_BASE_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '/api/method';

// In the multi-tenant SaaS pool the site MUST be resolved from the request
// Host (the tenant's domain), so we do NOT hardcode X-Frappe-Site-Name —
// pinning it (e.g. 'pos.local') makes Frappe 404 for every tenant.
// For local/dev you can still pin a site via REACT_APP_FRAPPE_SITE.
const defaultHeaders = {
  'Content-Type': 'application/json',
  'Accept': 'application/json',
};
if (process.env.REACT_APP_FRAPPE_SITE) {
  defaultHeaders['X-Frappe-Site-Name'] = process.env.REACT_APP_FRAPPE_SITE;
}

// Global request timeout. Without one, a slow/stuck backend or network makes
// every request hang indefinitely and the UI appears frozen ("system hangs").
// 60s is generous enough that legitimately heavy operations (sale submit with
// stock postings, reports, bulk import) still complete on a slow server, while
// a genuinely hung request is turned into a clear error instead of a freeze.
// Individual calls can override this via `config.timeout` (e.g. bulk import).
export const DEFAULT_TIMEOUT_MS = 60000;

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: defaultHeaders,
  withCredentials: true,
  timeout: DEFAULT_TIMEOUT_MS,
});

const toUserMessage = (error) => extractErrorMessage(error);

const notifyApiError = (error) => {
  // Optional escape hatch for endpoints that intentionally handle their own UX
  if (error?.config?.skipGlobalErrorNotification) return;

  let message = toUserMessage(error);
  let title = 'Request failed';

  if (error.response?.status === 403) {
    message = "You do not have permission to perform this action.";
    title = 'Permission Denied';
  }

  // No response = timeout or network failure. Give the till operator a clear,
  // actionable message instead of a silent freeze.
  const isTimeout = error?.code === 'ECONNABORTED' || /timeout/i.test(error?.message || '');
  if (isTimeout) {
    title = 'Request timed out';
    message = 'The server took too long to respond. Please check your connection and try again.';
  } else if (!error?.response && error?.request) {
    title = 'Connection problem';
    message = 'Could not reach the server. Please check your network connection and try again.';
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent('global-api-error', {
        detail: {
          title,
          message,
          severity: 'error',
          duration: 3000,
          playSound: false,
        },
      })
    );
  }
};

// Add JWT to every request except login
axiosInstance.interceptors.request.use(
  (config) => {
    // Endpoints that should NOT have Authorization header (guest access)
    const publicEndpoints = [
      'techsavanna_pos.api.auth_api.login_user',
      'techsavanna_pos.api.auth_api.login_with_pin',
      'techsavanna_pos.api.auth_api.reset_password',
      'techsavanna_pos.api.warehouse_api.list_warehouses_for_login',
      'techsavanna_pos.api.industry_api.get_pos_industries',
      'techsavanna_pos.api.product_seeding.get_pos_industries',
      'techsavanna_pos.api.verification_api.send_email_verification',
      'techsavanna_pos.api.verification_api.verify_email_code',
      // Product seeding (seed_products, bulk_upload_products, create_seed_item)
      // runs during onboarding while the user is logged in, and the backend
      // refuses it without a token, so it is deliberately not in this list.
    ];

    // Check if this is a public endpoint
    const isPublicEndpoint = publicEndpoints.some(endpoint =>
      config.url?.includes(endpoint)
    );

    // Only add Authorization header for authenticated endpoints
    if (!isPublicEndpoint) {
      const token = localStorage.getItem('access_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    // For FormData bodies, never force a Content-Type: axios must set
    // multipart/form-data WITH a boundary itself, otherwise the server can't
    // parse the fields/file (e.g. product image upload). Strip any inherited
    // JSON default so axios's FormData serializer takes over.
    const isFormData = typeof FormData !== 'undefined' && config.data instanceof FormData;
    if (isFormData) {
      if (config.headers && typeof config.headers.delete === 'function') {
        config.headers.delete('Content-Type');
      } else if (config.headers) {
        delete config.headers['Content-Type'];
      }
    } else if (!config.headers['Content-Type']) {
      // Ensure Content-Type is set for normal JSON requests
      config.headers['Content-Type'] = 'application/json';
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Handle response errors according to API documentation
axiosInstance.interceptors.response.use(
  (response) => {
    // Log successful responses in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`✅ ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`);
    }
    return response;
  },
  (error) => {
    // Handle different status codes according to API documentation
    if (error.response) {
      const status = error.response.status;

      // Extract error message safely
      const responseData = error.response.data;
      let errorMessage = '';
      if (typeof responseData === 'string') {
        errorMessage = responseData;
      } else if (responseData?.message) {
        if (typeof responseData.message === 'string') {
          errorMessage = responseData.message;
        } else if (typeof responseData.message === 'object') {
          errorMessage = responseData.message.error || responseData.message.message || JSON.stringify(responseData.message);
        }
      } else if (responseData?.exception) {
        errorMessage = typeof responseData.exception === 'string' ? responseData.exception : JSON.stringify(responseData.exception);
      }
      const errStr = (errorMessage || '').toLowerCase();

      // Check if it's a session expiration or unauthorized error.
      // NOTE: a 403 "not permitted to access this resource" is a per-DocType
      // PERMISSION denial, NOT a session expiry — treating it as one logs a
      // perfectly-authenticated user out the moment any background call hits a
      // resource their role can't read (the "keeps logging out in <1 min" bug).
      // Only true session-invalid signals should force logout.
      const isSessionExpired = status === 401 || (status === 403 && (
        errStr.includes('login to access') ||
        errStr.includes('session expired') ||
        errStr.includes('not logged in') ||
        errStr.includes('session has expired')
      ));

      if (isSessionExpired) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        localStorage.removeItem('api_key');
        localStorage.removeItem('api_secret');
        localStorage.removeItem('expires_at');

        // Suppress the toast/alert notifications for session timeouts
        if (error.config) {
          error.config.skipGlobalErrorNotification = true;
        }

        const currentPath = window.location.pathname;
        const isPublicPage = currentPath.includes('/login') ||
          currentPath === '/';
        const isOnboardingPage = currentPath.includes('/onboarding');

        // Trigger unauthorized redirect
        if (!isPublicPage && !isOnboardingPage) {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('unauthorized-error'));
          }
        }
      }

      // Log error responses in development
      if (process.env.NODE_ENV === 'development') {
        console.error(`${error.config?.method?.toUpperCase()} ${error.config?.url} - ${status}`, error.response.data);
      }
    } else if (error.request) {
      // Request was made but no response received
      // This often indicates a CORS issue
      const errorMessage = error.message || '';
      const isCorsError = errorMessage.includes('CORS') ||
        errorMessage.includes('Access-Control-Allow-Origin') ||
        errorMessage.includes('blocked by CORS policy');

      if (isCorsError) {
        // Check for duplicate CORS header issue
        const isDuplicateHeaderError = errorMessage.includes('multiple values') &&
          errorMessage.includes('Access-Control-Allow-Origin');

        if (isDuplicateHeaderError) {
          console.error('CORS Error: Duplicate Access-Control-Allow-Origin headers detected.');
          console.error('This is a server-side configuration issue. The server is sending the');
          console.error('Access-Control-Allow-Origin header multiple times, which browsers reject.');
          console.error('Request URL:', error.config?.url);
          console.error('Request Method:', error.config?.method);
          console.error('Solution: Fix server CORS configuration to send the header only once.');
          console.error('Common causes:');
          console.error('  - Multiple CORS middleware layers');
          console.error('  - Reverse proxy and application both adding headers');
          console.error('  - CORS configured in multiple places');
        } else {
          console.error('CORS Error: The request was blocked by CORS policy.');
          console.error('Request URL:', error.config?.url);
          console.error('Request Method:', error.config?.method);
          console.error('Check server CORS configuration.');
        }
      } else {
        console.error('Network error - No response received:', error.request);
      }
    } else {
      // Something else happened
      console.error('Error:', error.message);
    }

    notifyApiError(error);

    return Promise.reject(error);
  }
);

export default axiosInstance;

